/**
 * @fileoverview Mock backend for the Sales Dashboard.
 * @description Registers GET /sales/dashboard on the feature's shared mock adapter and answers in
 * the backend's envelope with its error codes (Confluence 3.5.11.40). The call takes no parameter
 * (any is 400 / 0001) and the caller decides the scope; a caller with no Sales role is refused with
 * 403 / 2008. Every section is projected on the spot from the stores the owning pages read (see
 * sales-dashboard-mock-sections.ts), so the dashboard always agrees with them. A no-op when
 * VITE_ENABLE_MOCK_API is false.
 * @copyright Copyright (c) 2026 Brownbox Consulting. All rights reserved.
 * @author Anshul Rokde
 */

import { callerOrRefusal } from './active-clients-mock-guard'
import { failReply, okEnvelope } from './sales-crm-mock-response.utils'
import {
  activeClientsSection,
  backlogSection,
  myPageSection,
  performanceSections,
  salesTeamSection,
  userManagementSection,
} from './sales-dashboard-mock-sections'
import { salesMockAdapter } from './sales-mock-adapter'
import { peopleInScope } from './sales-performance-mock-model'

import type { MockReply } from './sales-crm-mock-response.utils'
import type { SalesDashboard } from '@/features/sales/types/sales-dashboard.types'
import type { AxiosRequestConfig } from 'axios'

/**
 * @description Answers Get Sales Dashboard.
 * @param {AxiosRequestConfig} config - The request config; a query parameter or a body is refused.
 * @returns {MockReply} The dashboard, or the backend's error.
 */
export function dashboardReply(config: AxiosRequestConfig): MockReply {
  if (Object.keys((config.params ?? {}) as object).length > 0 || config.data) {
    return failReply(400, '0001', 'Invalid request')
  }
  const caller = callerOrRefusal()
  if (Array.isArray(caller)) return caller
  const { achievement, needsAttention } = performanceSections(caller, peopleInScope(caller))
  const dashboard: SalesDashboard = {
    greetingName: caller.name.split(' ')[0] ?? null,
    scope: achievement.scope,
    achievement,
    needsAttention,
    activeClients: activeClientsSection(),
    backlog: backlogSection(),
    myPage: myPageSection(caller),
    salesTeam: salesTeamSection(caller),
    userManagement: userManagementSection(),
  }
  return [200, okEnvelope(dashboard, { evaluatedAt: new Date().toISOString() })]
}

if (salesMockAdapter) {
  salesMockAdapter.onGet('/sales/dashboard').reply(dashboardReply)
}
